import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useFormatCurrency } from "@/context/CountryContext";
import { useGeo } from "@/hooks/useGeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search as SearchIcon, MapPin, ArrowRight, Store } from "lucide-react";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const fmt = useFormatCurrency();
  const { geo } = useGeo();
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [sort, setSort] = useState("price");
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = { sort };
      if (q) p.q = q;
      if (category && category !== "all") p.category = category;
      if (geo) { p.lat = geo.lat; p.lng = geo.lng; }
      const { data } = await api.get("/products", { params: p });
      setItems(data);
    } finally { setLoading(false); }
  }, [q, category, sort, geo]);

  useEffect(() => { load(); }, [category, sort, geo]); // eslint-disable-line

  const submit = (e) => {
    e.preventDefault();
    setParams({ q, ...(category !== "all" ? { category } : {}) });
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <form onSubmit={submit} className="bg-card border border-border rounded-xl p-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex items-center gap-2 flex-1">
          <SearchIcon className="w-5 h-5 text-muted-foreground ml-1" />
          <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Que cherchez-vous ?" className="border-0 shadow-none focus-visible:ring-0" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="cat-filter" className="sm:w-52"><SelectValue placeholder="Catégorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes catégories</SelectItem>
            {cats.map((c) => <SelectItem key={c.cat_id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="sort-filter" className="sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="price">Prix croissant</SelectItem>
            <SelectItem value="distance">Distance</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="search-submit" type="submit" className="bg-primary hover:bg-primary/90">Rechercher</Button>
      </form>

      <div className="flex items-center justify-between mt-6 mb-4">
        <h1 className="font-display text-xl font-bold text-secondary">{loading ? "Recherche…" : `${items.length} résultat(s)`}</h1>
        {geo && <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />Distances depuis votre position</span>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((p, i) => {
          const min = Math.min(...items.filter((x) => x.normalized_name === p.normalized_name).map((x) => x.price));
          const isBest = p.price === min;
          return (
            <div key={p.product_id} data-testid={`product-${p.product_id}`}
              onClick={() => navigate(`/comparer?name=${encodeURIComponent(p.name)}`)}
              className="cursor-pointer bg-card border border-border rounded-lg overflow-hidden hover:-translate-y-1 transition-transform animate-fade-up"
              style={{ animationDelay: `${i * 40}ms` }}>
              <div className="h-36 bg-white flex items-center justify-center overflow-hidden p-1">
                {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-contain" /> : <Store className="w-10 h-10 text-muted-foreground/40" />}
              </div>
              <div className="p-3">
                <div className="flex items-start justify-between gap-1">
                  <h3 className="font-medium text-sm text-secondary line-clamp-2">{p.name}</h3>
                  {isBest && <Badge className="bg-accent text-accent-foreground shrink-0 text-[10px]">Meilleur prix</Badge>}
                </div>
                <div className="mt-2 font-display font-bold text-lg text-primary">{fmt(p.price)}</div>
                <div className="text-xs text-muted-foreground">/ {p.unit}</div>
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="truncate">{p.shop_name}</span>
                  {p.distance_km != null && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{p.distance_km} km</span>}
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground/60 flex items-center justify-between">
                  <span>Source : {p.shop_name || "marché local"}</span>
                  {p.created_at && <span>Màj {new Date(p.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {!loading && items.length === 0 && (
        <div className="text-center py-20 text-muted-foreground">Aucun produit trouvé. Essayez un autre mot-clé.</div>
      )}
    </div>
  );
}
